import React from "react";
import { MH, MW, HG, PAD } from "../../utils/bracketLogic";
import { CHAMP_W, resetActive, getChampion } from "../../utils/doubleElimination";

// Pure renderer for a double-elimination bracket state.
//  - state: the engine state ({ matches, byId, ... })
//  - onPick(matchId, winnerName): called when a slot is clicked (omit for read-only)
//  - onSelectPlayer(name): in read-only mode, makes player names clickable
//  - stageRef: ref attached to the full-size stage div (for PDF export)
function DoubleBracketCanvas({ state, onPick, onSelectPlayer, stageRef }) {
  const { byId } = state;
  const champion = getChampion(state);
  const showReset = resetActive(state);

  // Which matches to draw.
  const visible = state.matches.filter((m) => {
    if (m.id === "GFR") return showReset || m.team1 !== undefined;
    return true;
  });

  // Canvas size.
  const maxBottom = Math.max(...visible.map((m) => m.y + MH / 2));
  const gf = byId["GF"];
  const lastX = showReset ? byId["GFR"].x : gf.x;
  const champX = lastX + MW + HG;
  const champY = gf.y;
  const svgW = champX + CHAMP_W + PAD;
  const svgH = Math.max(maxBottom, champY + MH / 2) + PAD;

  // Section labels.
  const wbYs = state.matches.filter((m) => m.bracket === "W").map((m) => m.y);
  const lbMatches = state.matches.filter((m) => m.bracket === "L");
  const winnersLabelY = Math.max(0, Math.min(...wbYs) - MH / 2 - 22);
  const losersLabelY = lbMatches.length
    ? Math.min(...lbMatches.map((m) => m.y)) - MH / 2 - 22
    : null;

  // Winner-advance connectors (loser drops are implied, like print templates).
  const edges = [];
  for (const m of visible) {
    if (!m.winnerTo) continue;
    const dest = byId[m.winnerTo.id];
    if (!visible.includes(dest)) continue;
    edges.push([m, dest, m.decided]);
  }
  if (showReset) edges.push([gf, byId["GFR"], gf.decided]);

  function Slot({ m, which }) {
    const team = which === 1 ? m.team1 : m.team2;
    const score = which === 1 ? m.s1 : m.s2;
    const isReal = typeof team === "string";
    const isBye = team === null;
    const canPick =
      !!onPick &&
      !m.decided &&
      typeof m.team1 === "string" &&
      typeof m.team2 === "string";
    const isWinner = m.decided && isReal && m.winner === team;
    const canSelect = !canPick && !!onSelectPlayer && isReal;
    const cls = [
      "tn-slot",
      isWinner ? "w" : "",
      isBye ? "bye" : "",
      canPick ? "pick" : "",
    ]
      .join(" ")
      .trim();
    return (
      <div className={cls} onClick={(e) => canPick && onPick(m.id, team, e)}>
        {canSelect ? (
          <span
            className="tn-sn tn-sn-link"
            onClick={() => onSelectPlayer(team)}
            title={`View ${team}'s record`}
          >
            {team}
          </span>
        ) : (
          <span className="tn-sn">{isReal ? team : isBye ? "BYE" : ""}</span>
        )}
        {score != null && <span className="tn-score">{score}</span>}
        {isWinner && <span className="tn-arr">▶</span>}
      </div>
    );
  }

  return (
    <div className="tn-bscroll">
      <div
        ref={stageRef}
        style={{ position: "relative", width: svgW, height: svgH }}
      >
        <svg
          style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
          width={svgW}
          height={svgH}
        >
          {edges.map(([from, to, won], i) => {
            const x1 = from.x + MW;
            const y1 = from.y;
            const x2 = to.x;
            const y2 = to.y;
            const mx = x1 + HG / 2;
            return (
              <polyline
                key={i}
                points={`${x1},${y1} ${mx},${y1} ${mx},${y2} ${x2},${y2}`}
                fill="none"
                stroke={won ? "var(--color-won-border)" : "var(--color-border)"}
                strokeWidth={2}
              />
            );
          })}
          {/* connector into the champion box */}
          <polyline
            points={`${lastX + MW},${champY} ${champX},${champY}`}
            fill="none"
            stroke={champion ? "var(--color-won-border)" : "var(--color-border)"}
            strokeWidth={2}
          />
        </svg>

        {/* Section labels */}
        <div className="tn-de-band" style={{ left: PAD, top: winnersLabelY }}>
          Winners Bracket
        </div>
        {losersLabelY != null && (
          <div className="tn-de-band" style={{ left: PAD, top: losersLabelY }}>
            Losers Bracket
          </div>
        )}

        {visible.map((m) => (
          <div
            key={m.id}
            className={`tn-match${m.decided && m.winner ? " won" : ""}`}
            style={{
              left: m.x,
              top: m.y - MH / 2,
              width: MW,
              height: MH,
            }}
          >
            <Slot m={m} which={1} />
            <div className="tn-divider" />
            <Slot m={m} which={2} />
          </div>
        ))}

        {/* Champion box */}
        <div
          className={`tn-champ-slot${champion ? " filled" : ""}`}
          style={{
            left: champX,
            top: champY - MH / 2,
            width: CHAMP_W,
            height: MH,
          }}
        >
          {champion ? (
            <span className="tn-champ-name">{champion}</span>
          ) : (
            <span className="tn-champ-empty">TBD</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default DoubleBracketCanvas;
